/**
 * Utility functions for handling Access Group authentication flow
 */

/**
 * Check if the current URL suggests we're returning from Access Group authentication
 */
export function isReturningFromLogin(): boolean {
  // Check for common Access Group return parameters
  const urlParams = new URLSearchParams(window.location.search);
  const hash = window.location.hash;
  
  // Access Group might return with various parameters
  const hasAuthParams = urlParams.has('code') || 
                       urlParams.has('token') || 
                       urlParams.has('access_token') ||
                       urlParams.has('id_token') ||
                       hash.includes('access_token') ||
                       hash.includes('id_token');
  
  return hasAuthParams;
}

/**
 * Check for Access Group tokens in URL and extract them
 */
export function extractTokenFromUrl(): string | null {
  // Check URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  
  // Check for token in query parameters
  let token = urlParams.get('access_token') || 
              urlParams.get('token') || 
              urlParams.get('id_token');
  
  if (token) {
    return token;
  }
  
  // Check hash fragment (common for OAuth flows)
  const hash = window.location.hash.substring(1);
  const hashParams = new URLSearchParams(hash);
  
  token = hashParams.get('access_token') || 
          hashParams.get('token') || 
          hashParams.get('id_token');
  
  return token;
}

/**
 * Clean up URL after extracting authentication parameters
 */
export function cleanupUrlAfterAuth(): void {
  // Remove auth-related parameters from URL
  const url = new URL(window.location.href);
  
  // Remove query parameters
  url.searchParams.delete('access_token');
  url.searchParams.delete('token');
  url.searchParams.delete('id_token');
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  
  // Remove hash fragment
  url.hash = '';
  
  // Update URL without page reload
  window.history.replaceState({}, document.title, url.toString());
}

/**
 * Extract and process any authentication token from the current URL
 */
export async function processAuthenticationReturn(): Promise<string | null> {
  if (!isReturningFromLogin()) {
    return null;
  }
  
  const token = extractTokenFromUrl();
  
  if (token) {
    // Clean up the URL
    cleanupUrlAfterAuth();
    return token;
  }
  
  return null;
}
